import { inject, Injectable } from '@angular/core';
import {
  createAddress,
  ICustomerBankKonto,
  IGeschaeftsPartner,
  IOrderDetailsInfo,
  IPartner,
  IPersonalData,
  IPhysischePerson,
  IProduktDetails,
  IRelocationDataDTO,
  IRelocationDTOV2,
  IVertragskonto,
  Product,
  toReferrencedFiles,
} from '@mwe/models';
import {
  encryptIBAN,
  getFromOlavKey,
  isProductCategoryFernwaerme,
  mapAddressToCustomerAdressDetails,
  sortProductsEnumCategory,
} from '@mwe/utils';
import { TariffSelectionCardLogic, TariffSelectionStore } from '@mwe/ui/components';
import { TariffChangeService } from '../tariff/tariff-change.service';
import { AccountLogic } from '../account/account.logic';
import { RelocationStore } from './relocation.store';

@Injectable({
  providedIn: 'root',
})
export class RelocationLogic {
  private readonly relocationStore = inject(RelocationStore);
  private readonly accountLogic = inject(AccountLogic);
  private readonly tariffSelectionCardLogic = inject(TariffSelectionCardLogic);
  private readonly tariffChangeService = inject(TariffChangeService);
  private readonly tariffSelectionStore = inject(TariffSelectionStore);

  async initTariffSelection(): Promise<void> {
    this.tariffSelectionCardLogic.initializeStoreData({
      allProducts: this.relocationStore.allSelectedProducts(),
      allPriceIndications: this.relocationStore.allPriceIndications(),
      zipCode: this.relocationStore.relocationToOlav().plz,
      userSelectedPriceIndications: this.relocationStore.selectedProductsPriceIndications(),
      userSelectedItems: this.relocationStore.selectedTariffs() ? this.relocationStore.selectedProducts() : [],
      defaultProducts: this.tariffChangeService.getConfigDefaultTariffs(),
      additionalOptionGroupName: 'Zusatzoptionen',
    });
    await this.tariffSelectionCardLogic.checkInitialVoucherCode();
  }

  async encryptDetails(details: IOrderDetailsInfo[]): Promise<IOrderDetailsInfo[]> {
    const hashCode = this.relocationStore.relocationFromAddress()?.hashCode;
    if (!hashCode)
      throw new Error(
        'Can not encrypt details because hashCode is not set. Please make sure that the address is loaded before encrypting the details.',
      );
    const encryptionTasks = details.map(async detail => {
      return {
        ...detail,
        produktDetails: {
          ...detail.produktDetails,
          vertragskonto: detail.produktDetails?.vertragskonto
            ? ((await encryptIBAN(detail.produktDetails.vertragskonto, hashCode)) as IVertragskonto)
            : detail.produktDetails?.vertragskonto,
        },
        verrechnungskonto: {
          ...detail.verrechnungskonto,
          eingangskonto: detail.verrechnungskonto?.eingangskonto
            ? ((await encryptIBAN(detail.verrechnungskonto.eingangskonto, hashCode)) as ICustomerBankKonto)
            : detail.verrechnungskonto?.eingangskonto,
          ausgangskonto: detail.verrechnungskonto?.ausgangskonto
            ? ((await encryptIBAN(detail.verrechnungskonto.ausgangskonto, hashCode)) as ICustomerBankKonto)
            : detail.verrechnungskonto?.ausgangskonto,
        },
      } as IOrderDetailsInfo;
    });

    return Promise.all(encryptionTasks);
  }

  isThereOnlyOneUniqueCustomer(products: Product[]): boolean {
    if (!Array.isArray(products) || products.length <= 1) {
      return true;
    }

    const customerNumbers = products.map(product => product.customerNumber);
    const uniqueCustomerNumbers = Array.from(new Set(customerNumbers));

    return uniqueCustomerNumbers.length === 1;
  }

  getUniqueCustomerNumbersWithStatus(): string[] {
    const customerAccountNumbers = this.relocationStore.uniqueCustomerNumbers();
    const moveOutAccounts: string[] = [];
    const newClientAccounts: string[] = [];
    const newClientGpIds: string[] = [];

    for (const caNumber of customerAccountNumbers) {
      const ids = caNumber.split('_');

      let details = this.relocationStore
        .relocationDetails()
        .filter(rd => rd.customerNumber === ids[0] && rd.accountNumber === ids[1] && rd.status === 'Abmeldung');
      if (details?.length > 0) {
        moveOutAccounts.push(caNumber + '_Abmeldung_' + details.map(d => d.category).join(','));
      }

      if (!newClientGpIds.includes(ids[0])) {
        newClientGpIds.push(ids[0]);
        if (Number.isNaN(Number(ids[0]))) {
          details = this.relocationStore
            .relocationDetails()
            .filter(rd => Number.isNaN(Number(rd.customerNumber)) && rd.status === 'Anmeldung');
        } else {
          details = this.relocationStore.relocationDetails().filter(rd => rd.customerNumber === ids[0] && rd.status === 'Anmeldung');
        }
        const newProducts = sortProductsEnumCategory(
          details.map(d => d.category),
          true,
        );
        for (const category of newProducts) {
          newClientAccounts.push(ids[0] + '_Anmeldung_' + category);
        }
      }
    }
    return [...moveOutAccounts, ...newClientAccounts];
  }

  getRelocationDTOV2(): IRelocationDTOV2 {
    const mappingDataIds = this.getUniqueCustomerNumbersWithStatus();

    const defaultIBAN = this.relocationStore.relocationDetails().find(rd => rd.bezahlartIcon === 'sepa' && rd.status === 'Abmeldung')
      ?.verrechnungskonto?.eingangskonto.iban;
    const defaultERechnugsEmail = this.relocationStore.relocationDetails().find(rd => rd.eRechnungInfo && rd.status === 'Abmeldung')
      ?.eRechnungInfo?.rechnungEmail;

    const mappingData = mappingDataIds.map((dataIds, index) => {
      let moveOutInfo: IOrderDetailsInfo = null;
      let newClientInfo: IOrderDetailsInfo = null;

      const ids = dataIds.split('_');

      if (dataIds.includes('Abmeldung')) {
        moveOutInfo = this.relocationStore
          .relocationDetails()
          .find(rd => rd.customerNumber === ids[0] && rd.accountNumber === ids[1] && rd.status === 'Abmeldung');
      }

      const categories = ids.at(-1).split(',');

      if (dataIds.includes('Anmeldung')) {
        newClientInfo = this.relocationStore.relocationDetails().find(rd => rd.category === categories[0] && rd.status === 'Anmeldung');
      }

      const partnerData = this.getPartnerData(moveOutInfo, newClientInfo, defaultERechnugsEmail);

      const customerNumber = Number.isNaN(Number(ids[0])) ? null : ids[0];
      const isuTariffKey = newClientInfo?.tariff.shortcut || moveOutInfo?.tariff.name;
      return {
        orderIdx: index,
        customerNumber: customerNumber,
        accountNumber: moveOutInfo ? ids[1] : null,
        businessPartnerNumber: customerNumber,
        categories: categories,
        isuTariffKeys: [isuTariffKey],
        tariffKeys: [newClientInfo?.tariff.key],
        tariffName: newClientInfo?.tariff.name,
        tariffClasses: moveOutInfo ? [moveOutInfo.availabilityDetailsFernwaerme] : [this.getAvailabilitiesForFWProduct(newClientInfo)],
        tarifOptionen: newClientInfo?.tarifOptionen,
        condition: newClientInfo?.condition,
        zustelladresse: newClientInfo?.zustelladresse,
        personalData: partnerData,
        invoiceAddress: mapAddressToCustomerAdressDetails(newClientInfo?.invoiceAddress || this.relocationStore.relocationToAddress()),
        changeERechnung: !!defaultERechnugsEmail,
        changeSepaMandat: !!defaultIBAN,
        processAdvice: ids.at(-2),
      };
    });

    return {
      oldAddress: this.relocationStore.relocationFromAddress(),
      newAddressOlav: this.relocationStore.relocationToOlav(),
      deregistrationDate: this.relocationStore.deregistrationDate(),
      registrationDate: this.relocationStore.registrationDate(),
      idCard: this.relocationStore.idCard(),
      referencedFiles: this.relocationStore.uploadedFiles() ? toReferrencedFiles(this.relocationStore.uploadedFiles().uploadedFiles) : null,
      pdfLinks: this.relocationStore.pdfLinks(),
      priceIndications: this.relocationStore.selectedProductsPriceIndications(),
      mappingData: mappingData,
      eRechnungsInfo: defaultERechnugsEmail ? { isERechnungsEmpfaenger: true, rechnungEmail: defaultERechnugsEmail } : null,
      sepaMandat: defaultIBAN ? { iban: defaultIBAN } : null,
      noOnlineCategories: this.relocationStore.noOnlineCategories(),
      noLongerAvailableCategories: this.relocationStore.noLongerAvailableCategories(),
      version: 'V2',
      selectedTariffs: this.tariffSelectionStore.selectedTariffs(),
    };
  }

  getPersonalDataFromPartner = (partnerDetail: IPartner, email: string, birthday: string): IPersonalData => {
    return {
      salutation: partnerDetail.anrede,
      title: { text: partnerDetail?.titel, value: '' },
      firstName: partnerDetail.vorname,
      lastName: partnerDetail.nachname,
      birthday: birthday?.replace('T00:00Z-', ''),
      telefonNumber: partnerDetail.telefon,
      telefonCountryCode: partnerDetail.telefonvorwahl,
      email: partnerDetail.email || email,
    };
  };

  getProductsForNoOnlineCategories(orderDetails: IOrderDetailsInfo[], noOnlineCategories: string[]): Product[] {
    const noOnlineProducts = [];
    if (noOnlineCategories) {
      for (const od of orderDetails) {
        if (noOnlineCategories.includes(od.category) && od.status === 'Abmeldung') {
          noOnlineProducts.push({ category: od.category, tariffClasses: [od.tariff?.name] });
        }
      }
    }
    return noOnlineProducts;
  }

  public parseVersionTwo(relocationData: any, relocationDetails: any) {
    const dto: IRelocationDataDTO = {
      selectedOlav: relocationData?.newAddressOlav,
      oldAddress: createAddress(relocationData?.oldAddress),
      newAddress: getFromOlavKey(relocationData?.newAddressOlav),
      deregistrationDate: relocationData?.deregistrationDate,
      registrationDate: relocationData?.registrationDate,
      relocationSubmittedAt: relocationDetails?.createdAt,
      idcard: relocationData?.idCard,
      referencedFiles: relocationData?.referencedFiles,
      useSubstituteDetails: relocationData?.useSubstituteDetails,
      pdfLinks: relocationData?.pdfLinks,
      noOnlineCategories: relocationData?.noOnlineCategories,
      noLongerAvailableCategories: relocationData?.noLongerAvailableCategories,
      version: relocationDetails.version,
      selectedTariffs: relocationData?.selectedTariffs ?? [],
    } as IRelocationDataDTO;

    this.createOrderDetailsInfo(relocationData, dto);

    dto.oldAddress.addressGroup.id = relocationDetails.addressGroupId;
    return dto;
  }

  public parseVersionOne(relocationData, relocationDetails: any) {
    const dto: IRelocationDataDTO = {
      selectedOlav: relocationData?.selectedOlav,
      oldAddress: createAddress(relocationData?.moveFromAddress ? relocationData?.moveFromAddress : relocationData?.oldAddress),
      newAddress: createAddress(relocationData?.moveToAddress ? relocationData?.moveToAddress : relocationData?.newAddress),
      deregistrationDate: relocationData?.deregistrationAt ? relocationData?.deregistrationAt : relocationData?.deregistrationDate,
      registrationDate: relocationData?.registrationAt ? relocationData?.registrationAt : relocationData?.registrationDate,
      relocationSubmittedAt: relocationDetails?.createdAt,
      orderDetailsInfo: relocationData?.relocationDetailsInfo ? relocationData?.relocationDetailsInfo : relocationData?.orderDetailsInfo,
      idcard: relocationData?.idcard,
      referencedFiles: relocationData?.referencedFiles,
      useSubstituteDetails: relocationData?.useSubstituteDetails,
      pdfLinks: relocationData?.pdfLinks,
      noOnlineCategories: relocationData?.noOnlineCategories,
    };
    dto.oldAddress.addressGroup.id = relocationDetails.addressGroupId;
    return dto;
  }

  private getAvailabilitiesForFWProduct(newClientInfo: IOrderDetailsInfo) {
    if (isProductCategoryFernwaerme(newClientInfo?.category)) {
      const fwDetails = this.relocationStore.relocationDetails().find(rd => rd.category === newClientInfo.category);
      return fwDetails?.availabilityDetailsFernwaerme;
    }
    return null;
  }

  private getPartnerData(moveOutInfo: IOrderDetailsInfo, newClientInfo: IOrderDetailsInfo, defaultERechnugsEmail) {
    const partner = moveOutInfo?.produktDetails.partner || newClientInfo?.produktDetails.partner;
    const birthday = moveOutInfo?.physischePerson?.geburtsdatum || newClientInfo?.physischePerson?.geburtsdatum;
    return partner
      ? this.getPersonalDataFromPartner(partner, defaultERechnugsEmail || this.accountLogic.getAccountEmail(), birthday)
      : null;
  }

  private createOrderDetailsInfo(relocationData: any, dto: IRelocationDataDTO) {
    const orderDetailsInfo: IOrderDetailsInfo[] = [];
    for (const md of relocationData.mappingData) {
      for (const category of md.categories) {
        orderDetailsInfo.push({
          status: md.processAdvice,
          accountNumber: md.accountNumber,
          customerNumber: md.customerNumber,
          category: category,
          apiVersion: md.version,
          tariff: md.isuTariffKeys?.[0] ? { shortcut: md.isuTariffKeys[0], key: md.tariffKeys[0], name: md.tariffName } : null,
          tarifOptionen: md.tarifOptionen,
          availabilityDetailsFernwaerme: md.tariffClasses?.[0],
          eRechnungInfo:
            relocationData.eRechnungsInfo?.isERechnungsEmpfaenger === true
              ? relocationData.eRechnungsInfo
              : { isERechnungsEmpfaenger: false },
          bezahlartIcon: relocationData.sepaMandat ? 'sepa' : 'zahlschein',
          bezahlartText: relocationData.sepaMandat?.iban || 'zahlschein',
          partnerDetails: this.getPartnerDetails(md),
          produktDetails: this.getProducktDetails(md),
          condition: md.condition,
          zustelladresse: md.zustelladresse,
          physischePerson: {
            geburtsdatum: md.personalData?.birthday,
            anrede: md.personalData?.salutation,
            vorname: md.personalData?.firstName,
            nachname: md.personalData?.lastName,
          },
        });
      }
    }
    dto.orderDetailsInfo = orderDetailsInfo;
  }

  private getProducktDetails(md) {
    return {
      partner: {
        partnerId: md.customerNumber,
        anrede: md.personalData?.salutation,
        vorname: md.personalData?.firstName,
        nachname: md.personalData?.lastName,
        email: md.personalData?.email,
        telefon: md.personalData?.telefonNumber,
        telefonvorwahl: md.personalData?.telefonCountryCode,
      } as IPartner,
    } as IProduktDetails;
  }

  private getPartnerDetails(md) {
    return {
      geburtsDatum: md.personalData?.birthday,
      physischePerson: {
        anrede: md.personalData?.salutation,
        vorname: md.personalData?.firstName,
        nachname: md.personalData?.lastName,
        email: md.personalData?.email,
        telefon: md.personalData?.telefonNumber,
        telefonvorwahl: md.personalData?.telefonCountryCode,
      } as IPhysischePerson,
    } as IGeschaeftsPartner;
  }
}
